import React, { useState, useEffect } from 'react';
import { Modal, TextField, Button, CircularProgress, Autocomplete, Checkbox, FormControlLabel } from '@mui/material';
import { Close } from '@mui/icons-material';

import { getAuthToken } from '../../utils/cookieUtils';
import DestinationFacilityFilter, {
  ALL_CAMPUS_FACILITIES,
  matchesFacilityFilter,
} from 'components/Campus/DestinationFacilityFilter';
import FacilityBadge from 'components/Campus/FacilityBadge';
import { facilityDisplayName, facilityLabelById, formatCampusLocation } from 'utils/campusLabels';
import { DestinationFacility, destinationFacilityId } from 'utils/destinationAvailability';
import { withCampusFacilities } from 'utils/movementVariables';
import { useShipmentChoice } from 'hooks/useShipmentChoice';
import { useCreatedByFacility } from 'hooks/useCreatedByFacility';
import { campusFromDestinationFacilities, withCreatedByFacility } from 'utils/createdByFacility';
// GraphQL API URL
const GRAPHQL_API_URL = process.env.REACT_APP_GRAPHQL_API || 'http://localhost:4000/api/graphql';

// Props interface
interface TrailerMoveModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (taskData: any) => void;
  onError: (error: string) => void;
  trailer: {
    uuid?: string;
    trailer_number?: string;
    trailerNumber?: string; // Alternative field name
    driverName?: string;
    referenceNumber?: string;
    carrier_name?: string;
    current_location?: any;
    active_lineage?: {
      trailer_state?: {
        shipment?: {
          reference_number?: string;
        };
      };
    };
  };
  title: string;
  /** The facility the trailer is at; the move's source. */
  facilityId: string;
  /** Campus facilities of the destinations; with 2+, a facility filter and `destination_facility_id` apply. */
  destinationFacilities?: DestinationFacility[];
  availableDockDoors?: any[];
  availableYardSpots?: any[];
  availableFacilityZones?: any[];
  zones?: any[];
  moveType?: 'dock_to_yard' | 'yard_to_dock' | 'any';
  allowLeaveFacility?: boolean;
  requireShipmentValidation?: boolean;
}

// Consecutive same-facility destinations, in order (they arrive grouped, anchor first).
function groupByFacility<T extends { facilityId?: string }>(items: T[]) {
  const groups: Array<{ facilityId?: string; items: T[] }> = [];
  items.forEach(item => {
    const last = groups[groups.length - 1];
    if (last && last.facilityId === item.facilityId) {
      last.items.push(item);
    } else {
      groups.push({ facilityId: item.facilityId, items: [item] });
    }
  });
  return groups;
}

const TrailerMoveModal: React.FC<TrailerMoveModalProps> = ({
  open,
  onClose,
  onSuccess,
  onError,
  trailer,
  title,
  facilityId,
  destinationFacilities = [],
  availableDockDoors = [],
  availableYardSpots = [],
  availableFacilityZones = [],
  zones = [],
  moveType = 'any',
  allowLeaveFacility = true,
  requireShipmentValidation = false
}) => {
  // Modal state
  const [selectedDestination, setSelectedDestination] = useState<string>('');
  const [selectedDestinationId, setSelectedDestinationId] = useState<string>('');
  const [selectedDestinationType, setSelectedDestinationType] = useState<'dock' | 'yard' | 'facility_zone' | 'leave'>('dock');
  const [selectedFacilityZone, setSelectedFacilityZone] = useState<string>('');
  const [shipmentReference, setShipmentReference] = useState<string>('');
  const [dockFilter, setDockFilter] = useState<string>('');
  const [yardFilter, setYardFilter] = useState<string>('');
  const [selectedZoneFilter, setSelectedZoneFilter] = useState<string | null>(null);
  const [autoComplete, setAutoComplete] = useState<boolean>(false);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [isValidatingShipment, setIsValidatingShipment] = useState<boolean>(false);
  const { chooseShipment, shipmentChoiceDialog } = useShipmentChoice();
  const { chooseCreatedByFacility, createdByFacilityDialog } = useCreatedByFacility();
  const [error, setError] = useState<string | null>(null);
  const [facilityFilter, setFacilityFilter] = useState<string>(ALL_CAMPUS_FACILITIES);

  const isMultiFacility = destinationFacilities.length > 1;
  const badgeFacility = (id?: string) => facilityLabelById(destinationFacilities, id);
  const inFacilityFilter = (item: { facilityId?: string }) => matchesFacilityFilter(item, facilityFilter);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setSelectedDestination('');
      setSelectedDestinationId('');
      setSelectedFacilityZone('');
      setDockFilter('');
      setYardFilter('');
      setSelectedZoneFilter(null);
      setAutoComplete(false);
      setFacilityFilter(ALL_CAMPUS_FACILITIES);
      setSelectedDestinationType(moveType === 'dock_to_yard' ? 'yard' : moveType === 'yard_to_dock' ? 'dock' : 'dock');
      // Load existing shipment reference from trailer data
      const existingReference = trailer.referenceNumber || 
                               trailer.active_lineage?.trailer_state?.shipment?.reference_number || 
                               '';
      setShipmentReference(existingReference);
      setError(null);
    }
  }, [open, trailer, moveType]);

  // Get trailer details for display
  const getTrailerDetails = () => {
    const details: { [key: string]: string } = {};
    
    if (trailer.trailer_number || trailer.trailerNumber) {
      details['Trailer'] = trailer.trailer_number || trailer.trailerNumber || '';
    }
    
    if (trailer.driverName) {
      details['Driver'] = trailer.driverName;
    }
    
    const referenceNumber = trailer.referenceNumber || trailer.active_lineage?.trailer_state?.shipment?.reference_number;
    if (referenceNumber) {
      details['Reference'] = referenceNumber;
    }
    
    if (trailer.carrier_name) {
      details['SCAC'] = trailer.carrier_name;
    }
    
    return details;
  };

  // Get available destinations based on move type
  const getAvailableDestinations = () => {
    if (selectedDestinationType === 'dock') {
      return availableDockDoors
        .filter(inFacilityFilter)
        .filter(door => {
          const hasValidName = (door.dockName || door.number) && (door.dockName || door.number).trim();
          const isAvailable = door.configuration?.status !== 'unavailable' && !door.isLocked;
          const matchesFilter = !dockFilter.trim() || (hasValidName && (door.dockName || door.number).toLowerCase().includes(dockFilter.toLowerCase()));
          return hasValidName && isAvailable && matchesFilter;
        })
        .map(door => ({
          id: door.dockUuid,
          label: door.dockName || door.number || door.id,
          zoneName: door.zoneName,
          ...door
        }));
    } else if (selectedDestinationType === 'yard') {
      return availableYardSpots
        .filter(inFacilityFilter)
        .filter(spot => {
          const hasValidName = spot.yard_spot_name && spot.yard_spot_name.trim();
          const isAvailable = spot.configuration?.status !== 'unavailable' && !spot.isLocked;
          const hasNoTrailer = !spot.trailer_name && !spot.isOccupied; // Filter out spots with trailers
          const matchesZone = !selectedZoneFilter || spot.facility_zone_uuid === selectedZoneFilter;
          const matchesFilter = !yardFilter.trim() || (hasValidName && spot.yard_spot_name.toLowerCase().includes(yardFilter.toLowerCase()));
          return hasValidName && isAvailable && hasNoTrailer && matchesZone && matchesFilter;
        })
        .map(spot => ({
          id: spot.yard_spot_uuid,
          label: spot.yard_spot_name,
          zoneName: spot.zoneName,
          ...spot
        }));
    }
    return [];
  };

  // Handle destination type change
  const handleDestinationTypeChange = (type: 'dock' | 'yard' | 'facility_zone' | 'leave') => {
    setSelectedDestinationType(type);
    setSelectedDestination('');
    setSelectedDestinationId('');
    setSelectedFacilityZone('');
    setDockFilter('');
    setYardFilter('');
    setSelectedZoneFilter(null);
    setError(null);
  };

  // Resolve the reference to one shipment id, asking which one is meant when it matches
  // more than one in the campus. A reference already on the trailer keeps that shipment.
  // Null means the user closed the prompt.
  const validateShipmentReference = async (referenceNumber: string): Promise<string | null> => {
    if (!referenceNumber.trim()) return null;

    setIsValidatingShipment(true);

    try {
      const choice = await chooseShipment(referenceNumber, facilityId, trailer.uuid, {
        preferCurrent: !!(trailer.referenceNumber || trailer.active_lineage?.trailer_state?.shipment?.reference_number)
      });

      if (choice.status === 'cancelled') return null;
      if (choice.status === 'none') {
        throw new Error(`No shipment found with reference number "${referenceNumber}". Please verify the reference number.`);
      }
      return choice.shipment.id;
    } catch (err: any) {
      console.error('Error validating shipment:', err);
      throw err;
    } finally {
      setIsValidatingShipment(false);
    }
  };

  // Handle move task creation
  const handleCreateMoveTask = async () => {
    if (!selectedDestinationType) {
      setError('Please select a destination type');
      return;
    }
    
    if (selectedDestinationType === 'facility_zone' && !selectedFacilityZone.trim()) {
      setError('Please select a facility zone');
      return;
    }
    
    if (selectedDestinationType !== 'leave' && selectedDestinationType !== 'facility_zone' && !selectedDestination.trim()) {
      setError('Please select a destination');
      return;
    }

    // Reference number is no longer required for dock moves, but show confirmation if empty
    if (requireShipmentValidation && !shipmentReference.trim()) {
      setError('Shipment reference number is required');
      return;
    }

    // Show confirmation dialog when moving to dock without a reference number
    if (selectedDestinationType === 'dock' && !shipmentReference.trim()) {
      const confirmed = window.confirm('Are you sure you want to move the trailer to dock without a shipment reference number?');
      if (!confirmed) {
        return;
      }
    }
    
    try {
      setIsCreating(true);
      setError(null);
      
      // Validate shipment reference if provided and get shipment ID
      let shipmentId = null;
      if (shipmentReference.trim()) {
        try {
          shipmentId = await validateShipmentReference(shipmentReference);
          if (!shipmentId) {
            setIsCreating(false);
            return;
          }
        } catch (validationError: any) {
          setError(validationError.message);
          setIsCreating(false);
          return;
        }
      }
      
      // Determine movement type based on current location and destination
      const currentLocationType = trailer.current_location?.__typename;
      const isCurrentlyInDock = currentLocationType === 'DockObject';
      const isCurrentlyInYard = currentLocationType === 'YardSpotObject';
      
      let movementType = 'warehouse_to_yard'; // Default fallback
      const variables: any = {
        trailer_uuid: trailer.uuid,
        facility_id: facilityId,
        force: false
        // movement_type will be set after we determine the correct type
      };

      // Add shipment ID if provided and validated
      if (shipmentId) {
        variables.shipment_id = shipmentId;
      }

      // Add autocomplete if checked
      if (autoComplete) {
        variables.auto_complete = true;
      }

      if (selectedDestinationType === 'dock') {
        const selectedDock = availableDockDoors.find(d => d.dockUuid === selectedDestinationId);
        
        
        if (selectedDock) {
          // Determine movement type based on current location
          if (isCurrentlyInDock) {
            movementType = 'warehouse_to_warehouse'; // dock to dock
          } else if (isCurrentlyInYard) {
            movementType = 'yard_to_warehouse'; // yard to dock
          } else {
            movementType = 'yard_to_warehouse'; // default fallback
          }
          
          variables.movement_type = movementType;
          variables.dock_uuid = selectedDock.dockUuid;
          variables.warehouse_zone_uuid = selectedDock.zoneUuid;
        }
      } else if (selectedDestinationType === 'yard') {
        const selectedSpot = availableYardSpots.find(s => s.yard_spot_uuid === selectedDestinationId);
        if (selectedSpot) {
          // Determine movement type based on current location
          if (isCurrentlyInYard) {
            movementType = 'yard_to_yard'; // yard to yard
          } else if (isCurrentlyInDock) {
            movementType = 'warehouse_to_yard'; // dock to yard
          } else {
            movementType = 'warehouse_to_yard'; // default fallback
          }
          
          variables.movement_type = movementType;
          variables.yard_spot_uuid = selectedSpot.yard_spot_uuid;
          variables.facility_zone_uuid = selectedSpot.facility_zone_uuid;
        }
      } else if (selectedDestinationType === 'facility_zone') {
        // Moving to facility zone - determine based on current location
        if (isCurrentlyInDock) {
          movementType = 'dock_to_facility_zone';
          // Set source dock information if available
          if (trailer.current_location?.uuid) {
            variables.dock_uuid = trailer.current_location.uuid;
          }
        } else if (isCurrentlyInYard) {
          movementType = 'yard_spot_to_facility_zone';
          // Set source yard spot information if available
          if (trailer.current_location?.uuid) {
            variables.yard_spot_uuid = trailer.current_location.uuid;
          }
          // Set source facility zone (the zone containing the yard spot) if available
          if (trailer.current_location?.facility_zone?.uuid) {
            variables.facility_zone_uuid = trailer.current_location.facility_zone.uuid;
          }
        } else {
          // If current location is unknown or already in a facility zone, assume facility_zone_to_facility_zone
          movementType = 'facility_zone_to_facility_zone';
          // Set source facility zone if available (for facility_zone_to_facility_zone)
          if (trailer.current_location?.uuid) {
            variables.source_facility_zone_uuid = trailer.current_location.uuid;
          }
        }
        variables.movement_type = movementType;
        variables.destination_facility_zone_uuid = selectedFacilityZone;
      } else if (selectedDestinationType === 'leave') {
        movementType = 'leaving_facility';
        variables.movement_type = movementType;
      }

      // Ensure movement_type is always set in variables
      variables.movement_type = movementType;

      // A destination at a sibling facility goes in destination_facility_id.
      const destinations = {
        dockDoors: availableDockDoors,
        yardSpots: availableYardSpots,
        facilityZones: availableFacilityZones,
      };
      const targetFacilityId =
        selectedDestinationType === 'dock'
          ? destinationFacilityId(destinations, 'dock', selectedDestinationId)
          : selectedDestinationType === 'yard'
            ? destinationFacilityId(destinations, 'yard_spot', selectedDestinationId)
            : selectedDestinationType === 'facility_zone'
              ? destinationFacilityId(destinations, 'facility_zone', selectedFacilityZone)
              : undefined;
      const createdBy = await chooseCreatedByFacility({
        ...campusFromDestinationFacilities(destinationFacilities),
        sourceFacilityId: facilityId,
        destinationFacilityId: targetFacilityId,
        summary: `Trailer ${trailer.trailer_number || trailer.trailerNumber || ''}`.trim()
      });
      if (createdBy.status === 'cancelled') return;

      const campusVariables = withCreatedByFacility(
        withCampusFacilities(variables, facilityId, targetFacilityId),
        createdBy.facilityId
      );


      // GraphQL mutation
      const mutation = `
        mutation CreateMovementTask(
          $trailer_uuid: String!,
          $movement_type: String!,
          $facility_id: ID!,
          $destination_facility_id: ID,
          $created_by_facility_id: ID,
          $dock_uuid: String,
          $yard_spot_uuid: String,
          $warehouse_zone_uuid: String,
          $facility_zone_uuid: String,
          $destination_facility_zone_uuid: String,
          $shipment_id: String,
          $driver_id: String,
          $force: Boolean,
          $auto_complete: Boolean
        ) {
          create_movement_task(input: {
            trailer_uuid: $trailer_uuid
            movement_type: $movement_type
            facility_id: $facility_id
            destination_facility_id: $destination_facility_id
            created_by_facility_id: $created_by_facility_id
            dock_uuid: $dock_uuid
            yard_spot_uuid: $yard_spot_uuid
            warehouse_zone_uuid: $warehouse_zone_uuid
            facility_zone_uuid: $facility_zone_uuid
            destination_facility_zone_uuid: $destination_facility_zone_uuid
            shipment_id: $shipment_id
            driver_id: $driver_id
            force: $force
            auto_complete: $auto_complete
          }) {
            uuid
            task_type {
              uuid
              type
            }
            task_status {
              status
            }
            trailer {
              trailer_number
            }
            from_location
            to_location
            data
          }
        }
      `;

      // Get auth token from cookie or localStorage
      const authToken = getAuthToken();

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: mutation,
          variables: campusVariables
        })
      });

      const result = await response.json();

      if (result.errors) {
        throw new Error(result.errors[0]?.message || 'Failed to create move task');
      }

      const taskData = result.data?.create_movement_task;
      if (!taskData) {
        throw new Error('No task data returned from server');
      }

      // Call success callback
      onSuccess(taskData);
      
    } catch (error: any) {
      console.error('Failed to create move task:', error);
      const errorMessage = error.message || 'Failed to create move task';
      setError(errorMessage);
      onError(errorMessage);
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const trailerDetails = getTrailerDetails();
  const availableDestinations = getAvailableDestinations();

  return (
    <>
    <Modal
      open={open}
      onClose={handleClose}
      className="flex justify-center items-center"
    >
      <div className="w-[450px] bg-white border p-5 max-h-[90vh] overflow-y-auto relative">
        <Close
          className="absolute top-4 right-4 cursor-pointer hover:text-slate-400"
          onClick={handleClose}
        />
        <h1 className="text-xl font-bold mb-4">{title}</h1>
        
        {/* Trailer details */}
        <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(trailerDetails).map(([key, value]) => (
              <div key={key} className={key === 'Reference' ? 'col-span-2' : ''}>
                <span className="font-semibold">{key}:</span> {value}
              </div>
            ))}
          </div>
        </div>
        
        {/* Show error if any */}
        {error && (
          <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
            {error}
          </div>
        )}
        
        {/* Shipment Reference Number field */}
        {(requireShipmentValidation || selectedDestinationType === 'dock' || shipmentReference) && (
          <div className="mb-4">
            <h2 className="font-semibold mb-2">
              Shipment Reference Number {requireShipmentValidation ? '*' : '(Optional)'}
            </h2>
            <TextField
              label={`Enter Reference Number ${requireShipmentValidation ? '*' : '(Optional)'}`}
              variant="outlined"
              fullWidth
              required={requireShipmentValidation}
              value={shipmentReference}
              onChange={(e) => setShipmentReference(e.target.value)}
              helperText={
                (trailer.referenceNumber || trailer.active_lineage?.trailer_state?.shipment?.reference_number) ? 
                "Reference number is read-only when already assigned" : 
                `${requireShipmentValidation ? 'Shipment reference number is required' : 'Enter the shipment reference number if available. For dock moves without a reference, you will be asked to confirm.'} (e.g. REF12345)`
              }
              disabled={isCreating || isValidatingShipment || !!(trailer.referenceNumber || trailer.active_lineage?.trailer_state?.shipment?.reference_number)}
              error={requireShipmentValidation && !shipmentReference.trim() && error?.includes('required')}
            />
          </div>
        )}
        
        {/* Auto-complete checkbox */}
        <div className="mb-4">
          <FormControlLabel
            control={
              <Checkbox
                checked={autoComplete}
                onChange={(e) => setAutoComplete(e.target.checked)}
                disabled={isCreating || isValidatingShipment}
              />
            }
            label="Auto-complete task (task will be marked as completed immediately)"
          />
        </div>
        
        {/* Destination Type Selection (only show if multiple types are allowed) */}
        {(moveType === 'any' || allowLeaveFacility) && (
          <div className="mb-4">
            <h2 className="font-semibold mb-2">Destination Type</h2>
            <div className="flex gap-2 flex-wrap">
              {(moveType === 'any' || moveType === 'dock_to_yard' || moveType === 'yard_to_dock') && (
                <>
                  <button
                    className={`px-3 py-2 rounded text-sm ${
                      selectedDestinationType === 'dock' 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-200 hover:bg-gray-300'
                    }`}
                    onClick={() => handleDestinationTypeChange('dock')}
                  >
                    Dock Door
                  </button>
                  <button
                    className={`px-3 py-2 rounded text-sm ${
                      selectedDestinationType === 'yard' 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-200 hover:bg-gray-300'
                    }`}
                    onClick={() => handleDestinationTypeChange('yard')}
                  >
                    Yard Spot
                  </button>
                  <button
                    className={`px-3 py-2 rounded text-sm ${
                      selectedDestinationType === 'facility_zone' 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-200 hover:bg-gray-300'
                    }`}
                    onClick={() => handleDestinationTypeChange('facility_zone')}
                  >
                    Facility Zone
                  </button>
                </>
              )}
              {allowLeaveFacility && (
                <button
                  className={`px-3 py-2 rounded text-sm ${
                    selectedDestinationType === 'leave' 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-gray-200 hover:bg-gray-300'
                  }`}
                  onClick={() => handleDestinationTypeChange('leave')}
                >
                  Leave Facility
                </button>
              )}
            </div>
          </div>
        )}
        
        {/* Destination Selection */}
        {selectedDestinationType !== 'leave' && (
          <div className="mb-4">
            <DestinationFacilityFilter
              facilities={destinationFacilities}
              value={facilityFilter}
              onChange={value => {
                setFacilityFilter(value);
                setSelectedDestination('');
                setSelectedDestinationId('');
                setSelectedFacilityZone('');
                setSelectedZoneFilter(null);
                setDockFilter('');
                setYardFilter('');
              }}
              disabled={isCreating || isValidatingShipment}
            />
            {selectedDestinationType === 'facility_zone' ? (
              /* Facility zone selection */
              <div>
                <h2 className="font-semibold mb-2">Select Facility Zone</h2>
                
                <div className="space-y-3">
                  {/* Quick selection from available facility zones */}
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Select from available facility zones:</div>
                    <div className="flex flex-wrap gap-2">
                      {(() => {
                        // Get available facility zones
                        const availableFacilityZonesArray = (availableFacilityZones || []).filter(inFacilityFilter);
                        
                        if (availableFacilityZonesArray.length === 0) {
                          return (
                            <div className="text-amber-600 p-2 border border-amber-200 rounded text-sm bg-amber-50 w-full">
                              No facility zones found.
                            </div>
                          );
                        }
                        
                        return availableFacilityZonesArray.map((zone: any) => {
                          return (
                            <button
                              key={zone.facility_zone_uuid || zone.id}
                              className={`px-3 py-2 border rounded text-sm ${
                                selectedFacilityZone === (zone.facility_zone_uuid || zone.id)
                                  ? 'bg-blue-100 border-blue-500' 
                                  : 'bg-gray-50 hover:bg-gray-100'
                              }`}
                              onClick={() => setSelectedFacilityZone(zone.facility_zone_uuid || zone.id)}
                            >
                              <div className="flex flex-col">
                                <span className="font-medium">{zone.facility_zone_name || zone.name}</span>
                                {zone.zone_type && (
                                  <span className="text-xs text-gray-500">{zone.zone_type}</span>
                                )}
                                <FacilityBadge facility={badgeFacility(zone.facilityId)} show={isMultiFacility} />
                              </div>
                            </button>
                          );
                        });
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* Dock Door or Yard Spot selection */
              <div>
                <h2 className="font-semibold mb-2">
                  Select {selectedDestinationType === 'dock' ? 'Dock Door' : 'Yard Spot'}
                </h2>
                
                {/* Zone filter dropdown for yard spots */}
                {selectedDestinationType === 'yard' && zones.filter(inFacilityFilter).length > 0 && (
                  <div className="mb-4">
                    <h3 className="font-semibold mb-2">Filter by Zone (Optional)</h3>
                    <Autocomplete
                      options={zones.filter(inFacilityFilter).map(zone => ({ 
                        label: formatCampusLocation(zone.name, badgeFacility(zone.facilityId), isMultiFacility), 
                        value: zone.id || zone.facility_zone_uuid,
                        color: zone.color 
                      }))}
                      value={selectedZoneFilter ? zones.find(z => (z.id || z.facility_zone_uuid) === selectedZoneFilter) ? { 
                        label: zones.find(z => (z.id || z.facility_zone_uuid) === selectedZoneFilter)!.name, 
                        value: selectedZoneFilter,
                        color: zones.find(z => (z.id || z.facility_zone_uuid) === selectedZoneFilter)!.color
                      } : null : null}
                      onChange={(_, newValue) => {
                        setSelectedZoneFilter(newValue?.value || null);
                        // Clear selected destination and yard filter when zone filter changes
                        setSelectedDestination('');
                        setSelectedDestinationId('');
                        setYardFilter('');
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Zone"
                          variant="outlined"
                          fullWidth
                          helperText="Filter yard spots by zone"
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <div className="flex items-center">
                            <div 
                              className="w-3 h-3 rounded mr-2" 
                              style={{ backgroundColor: option.color }}
                            />
                            {option.label}
                          </div>
                        </li>
                      )}
                    />
                  </div>
                )}
                
                <TextField
                  label={`Filter ${selectedDestinationType === 'dock' ? 'Dock Doors' : 'Yard Spots'}`}
                  variant="outlined"
                  fullWidth
                  value={selectedDestinationType === 'dock' ? dockFilter : yardFilter}
                  onChange={(e) => {
                    const filterValue = e.target.value;
                    
                    if (selectedDestinationType === 'dock') {
                      setDockFilter(filterValue);
                      
                      // Get available dock doors with valid names/numbers
                      const availableDoors = availableDockDoors
                        .filter(inFacilityFilter)
                        .filter(door => 
                          door.configuration?.status !== 'unavailable' && // Not unavailable
                          !door.isLocked && // Not locked (availability logic handled in parent)
                          (door.dockName || door.number) && // Has valid name/number
                          (door.dockName || door.number).trim() // Not empty
                        );
                      
                      // Auto-selection logic
                      if (filterValue.trim()) {
                        // Find doors that match the filter
                        const matchingDoors = availableDoors.filter(door => {
                          const doorName = door.dockName || door.number || '';
                          return doorName.toLowerCase().includes(filterValue.toLowerCase());
                        });
                        
                        // Check for exact match
                        const exactMatch = matchingDoors.find(door => {
                          const doorName = door.dockName || door.number || '';
                          return doorName.toLowerCase() === filterValue.toLowerCase();
                        });
                        
                        // Auto-select if there's exactly 1 result and it's an exact match
                        if (matchingDoors.length === 1 && exactMatch) {
                          setSelectedDestination(exactMatch.dockName || exactMatch.number || '');
                          setSelectedDestinationId(exactMatch.dockUuid);
                        } else {
                          // Clear selection if multiple results or no exact match
                          setSelectedDestination('');
                          setSelectedDestinationId('');
                        }
                      } else {
                        // Clear selection when filter is empty
                        setSelectedDestination('');
                        setSelectedDestinationId('');
                      }
                    } else {
                      setYardFilter(filterValue);
                      
                      // Get available yard spots with valid names/numbers
                      const availableSpots = availableYardSpots
                        .filter(inFacilityFilter)
                        .filter(spot => 
                          spot.configuration?.status !== 'unavailable' && // Not unavailable
                          !spot.isLocked && // Not locked (availability logic handled in parent)
                          spot.yard_spot_name && // Has valid name
                          spot.yard_spot_name.trim() && // Not empty
                          (!selectedZoneFilter || spot.facility_zone_uuid === selectedZoneFilter) // Matches zone filter
                        );
                      
                      // Auto-selection logic
                      if (filterValue.trim()) {
                        // Find spots that match the filter
                        const matchingSpots = availableSpots.filter(spot => {
                          const spotName = spot.yard_spot_name || '';
                          return spotName.toLowerCase().includes(filterValue.toLowerCase());
                        });
                        
                        // Check for exact match
                        const exactMatch = matchingSpots.find(spot => {
                          const spotName = spot.yard_spot_name || '';
                          return spotName.toLowerCase() === filterValue.toLowerCase();
                        });
                        
                        // Auto-select if there's exactly 1 result and it's an exact match
                        if (matchingSpots.length === 1 && exactMatch) {
                          setSelectedDestination(exactMatch.yard_spot_name || '');
                          setSelectedDestinationId(exactMatch.yard_spot_uuid);
                        } else {
                          // Clear selection if multiple results or no exact match
                          setSelectedDestination('');
                          setSelectedDestinationId('');
                        }
                      } else {
                        // Clear selection when filter is empty
                        setSelectedDestination('');
                        setSelectedDestinationId('');
                      }
                    }
                  }}
                  helperText={`Type to filter ${selectedDestinationType === 'dock' ? 'dock doors' : 'yard spots'} below (e.g. ${selectedDestinationType === 'dock' ? 'D10' : 'A15'})`}
                  placeholder={`Type ${selectedDestinationType === 'dock' ? 'dock door' : 'yard spot'} name to filter...`}
                />
                
                {/* Available destinations */}
                {availableDestinations.length > 0 && (
                  <div className="mt-3">
                    <div className="text-sm text-gray-600 mb-1">
                      Available {selectedDestinationType === 'dock' ? 'Dock Doors' : 'Yard Spots'}:
                    </div>
                    {groupByFacility(availableDestinations).map(group => (
                      <div key={group.facilityId || 'facility'} className="mb-2">
                        {isMultiFacility && facilityFilter === ALL_CAMPUS_FACILITIES && (
                          <div className="text-xs font-semibold text-indigo-700 mb-1">
                            {facilityDisplayName(badgeFacility(group.facilityId)) || group.facilityId}
                          </div>
                        )}
                        <div className="flex flex-wrap gap-2">
                          {group.items.map(dest => (
                            <button
                              key={dest.id}
                              className={`px-2 py-1 border rounded text-sm ${
                                selectedDestinationId === dest.id
                                  ? 'bg-blue-100 border-blue-500'
                                  : dest.hasMoveFromTask
                                    ? 'bg-gray-300 hover:bg-gray-400'
                                    : 'bg-gray-50 hover:bg-gray-100'
                              }`}
                              onClick={() => {
                                setSelectedDestination(dest.label);
                                setSelectedDestinationId(dest.id);
                                // Update the filter to match the selected destination
                                if (selectedDestinationType === 'dock') {
                                  setDockFilter(dest.label);
                                } else {
                                  setYardFilter(dest.label);
                                }
                              }}
                              title={dest.hasMoveFromTask ? 'Door has an active move-from task' : undefined}
                            >
                              <div className="flex flex-col">
                                <span className="font-medium">{dest.label}</span>
                                {dest.zoneName && (
                                  <span className="text-xs text-gray-500">{dest.zoneName}</span>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {selectedDestinationType === 'dock' && isMultiFacility && (
                  <div className="mt-2 text-xs text-gray-500">
                    Dock doors are listed only for facilities you are assigned to.
                  </div>
                )}

                {/* No available destinations message */}
                {availableDestinations.length === 0 && (
                  <div className="mt-3 text-amber-600 bg-amber-50 p-2 border border-amber-200 rounded text-sm">
                    {selectedDestinationType === 'dock' ? (
                      dockFilter.trim() 
                        ? `No dock doors found matching "${dockFilter}". Try clearing the filter.`
                        : `No available dock doors found.`
                    ) : (
                      yardFilter.trim() && selectedZoneFilter 
                        ? `No yard spots found matching "${yardFilter}" in the selected zone. Try clearing the filters.`
                        : yardFilter.trim()
                        ? `No yard spots found matching "${yardFilter}". Try clearing the filter.`
                        : selectedZoneFilter 
                        ? `No available yard spots found in the selected zone. Try clearing the zone filter.`
                        : `No available yard spots found.`
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Information text for leave facility */}
        {selectedDestinationType === 'leave' && (
          <div className="mb-4 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded p-2">
            No destination required - trailer will leave the facility
          </div>
        )}
        
        <div className="flex justify-between pt-3 border-t">
          <Button onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleCreateMoveTask}
            disabled={
              (selectedDestinationType === 'facility_zone' && !selectedFacilityZone.trim()) ||
              (selectedDestinationType !== 'leave' && selectedDestinationType !== 'facility_zone' && !selectedDestination.trim()) || 
              (requireShipmentValidation && !shipmentReference.trim()) ||
              isCreating ||
              isValidatingShipment
            }
          >
            {isValidatingShipment ? (
              <>
                <CircularProgress size={16} className="mr-2" />
                Validating...
              </>
            ) : isCreating ? (
              <>
                <CircularProgress size={16} className="mr-2" />
                Creating...
              </>
            ) : (
              `Move to ${
                selectedDestinationType === 'dock' ? 'Dock' : 
                selectedDestinationType === 'yard' ? 'Yard' : 
                selectedDestinationType === 'facility_zone' ? 'Zone' :
                'Exit'
              }`
            )}
          </Button>
        </div>
      </div>
    </Modal>
    {shipmentChoiceDialog}
    {createdByFacilityDialog}
    </>
  );
};

export default TrailerMoveModal;
